import classNames from 'classnames/bind';
import commaNumber from 'comma-number';
import React, { useMemo } from 'react';
import { MdOutlineFlipCameraAndroid, MdOutlineStorm } from 'react-icons/md';

import styles from './index.module.scss';

import Bar from '@/components/ui/Bar';
import { Cell } from '@/components/ui/dashboard/Monitor';
import { MAVLINK_MESSAGE_IDS as MESSAGE } from '@/define/messageId';
import useRobotTelemetry from '@/hooks/useRobotTelemetry';

const BATTERY_CURRENT_OFFSET = 30000;

const cx = classNames.bind(styles);

const Monitor = ({ robotId }) => {
  const vfrHud = useRobotTelemetry(robotId, MESSAGE.VFR_HUD);
  const usvData = useRobotTelemetry(robotId, 1101);

  const getAnimationDuration = (percent) => {
    if (percent === 0) {
      return '0s';
    }
    return `${(100 - percent) / 50 + 0.2}s`;
  };

  const groundspeed = useMemo(() => {
    return vfrHud?.groundspeed ?? 0;
  }, [vfrHud]);

  const shecoData = useMemo(() => {
    // prettier-ignore
    const nextData = {
      recoveredFlow: 0,   // 회수량
      battery1Remain: 0,  // 배터리 #1 잔량
      battery1Voltage: 0, // 배터리 #1 전압
      battery1Current: 0, // 배터리 #1 전류
      battery2Remain: 0,  // 배터리 #2 잔량
      battery2Voltage: 0, // 배터리 #2 전압
      battery2Current: 0, // 배터리 #2 전류
      propulsion1: 0,     // 추진체 #1 출력
      propulsion2: 0,     // 추진체 #2 출력
      recovery1: 0,       // 회수부 #1 출력
      recovery2: 0,       // 회수부 #2 출력
      recovery3: 0,       // 회수부 #3 출력
      recovery4: 0,       // 회수부 #4 출력
    };

    if (usvData) {
      nextData.recoveredFlow = Number(usvData.oilcollMotorEncCnt.toFixed(2));
      // 배터리
      nextData.battery1Remain = (usvData.bms1Soc / 10).toFixed(1);
      nextData.battery1Voltage = (usvData.bms1Vbat / 10).toFixed(1);
      nextData.battery1Current = Math.abs((usvData.bms1Curr - BATTERY_CURRENT_OFFSET) / 10).toFixed(1);
      nextData.battery2Remain = (usvData.bms2Soc / 10).toFixed(1);
      nextData.battery2Voltage = (usvData.bms2Vbat / 10).toFixed(1);
      nextData.battery2Current = Math.abs((usvData.bms2Curr - BATTERY_CURRENT_OFFSET) / 10).toFixed(1);
      // 추진체
      nextData.propulsion1 = usvData.thrUtilRate[0] ?? 0;
      nextData.propulsion2 = usvData.thrUtilRate[1] ?? 0;
      // 회수부
      nextData.recovery1 = usvData.oilcollMotorRotSpd[0] ?? 0;
      nextData.recovery2 = usvData.oilcollMotorRotSpd[1] ?? 0;
      nextData.recovery3 = usvData.oilcollMotorRotSpd[2] ?? 0;
      nextData.recovery4 = usvData.oilcollMotorRotSpd[3] ?? 0;
    }

    return nextData;
  }, [usvData]);

  return (
    <div className={cx('container')}>
      <div className={cx('boxes')}>
        <div className={cx(['box', 'battery'])}>
          <div className={cx('label')}>Battery Status</div>
          <div className={cx('items')}>
            <div className={cx('item')}>
              <div className={cx('battery')}>
                <div className={cx('bar')} style={{ width: `${shecoData.battery1Remain}%` }} />
                <div className={cx('value')}>
                  {shecoData.battery1Remain}
                  <span className={cx('unit')}>%</span>
                </div>
              </div>
              <div className={cx('info')}>
                <div className={cx('value')}>
                  {shecoData.battery1Voltage}
                  <span className={cx('unit')}>V</span>
                </div>
                <div className={cx('value')}>
                  {shecoData.battery1Current}
                  <span className={cx('unit')}>A</span>
                </div>
              </div>
            </div>
            <Bar size={20} />
            <div className={cx('item')}>
              <div className={cx('battery')}>
                <div className={cx('bar')} style={{ width: `${shecoData.battery2Remain}%` }} />
                <div className={cx('value')}>
                  {shecoData.battery2Remain}
                  <span className={cx('unit')}>%</span>
                </div>
              </div>
              <div className={cx('info')}>
                <div className={cx('value')}>
                  {shecoData.battery2Voltage}
                  <span className={cx('unit')}>V</span>
                </div>
                <div className={cx('value')}>
                  {shecoData.battery2Current}
                  <span className={cx('unit')}>A</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={cx('boxes')}>
        <Cell data={[{ title: 'Speed', value: commaNumber(groundspeed.toFixed(2)), unit: 'm/s' }]} />
        <Cell data={[{ title: 'Recovered Flow', value: shecoData.recoveredFlow, unit: 'L/h' }]} />
      </div>
      <div className={cx('boxes')}>
        <div className={cx(['box', 'motor'])}>
          <div className={cx('label')}>Propulsion</div>
          <div className={cx('items')}>
            <div className={cx('item')}>
              <MdOutlineStorm
                size={24}
                color="white"
                className={cx('rotate')}
                style={{ animationDuration: getAnimationDuration(shecoData.propulsion1) }}
              />
              <div className={cx('value')}>
                {shecoData.propulsion1}
                <span className={cx('unit')}>%</span>
              </div>
            </div>
            <div className={cx('item')}>
              <MdOutlineStorm
                size={24}
                color="white"
                className={cx('rotate')}
                style={{ animationDuration: getAnimationDuration(shecoData.propulsion2) }}
              />
              <div className={cx('value')}>
                {shecoData.propulsion2}
                <span className={cx('unit')}>%</span>
              </div>
            </div>
          </div>
        </div>
        <div className={cx(['box', 'motor'])} style={{ flex: 1.5 }}>
          <div className={cx('label')}>Recovery</div>
          <div className={cx('items')}>
            <div className={cx('item')}>
              <MdOutlineFlipCameraAndroid
                size={24}
                color="white"
                className={cx('rotate')}
                style={{ animationDuration: getAnimationDuration(shecoData.recovery1) }}
              />
              <div className={cx('value')}>
                {shecoData.recovery1}
                <span className={cx('unit')}>%</span>
              </div>
            </div>
            <div className={cx('item')}>
              <MdOutlineFlipCameraAndroid
                size={24}
                color="white"
                className={cx('rotate')}
                style={{ animationDuration: getAnimationDuration(shecoData.recovery2) }}
              />
              <div className={cx('value')}>
                {shecoData.recovery2}
                <span className={cx('unit')}>%</span>
              </div>
            </div>
            <div className={cx('item')}>
              <MdOutlineFlipCameraAndroid
                size={24}
                color="white"
                className={cx('rotate')}
                style={{ animationDuration: getAnimationDuration(shecoData.recovery3) }}
              />
              <div className={cx('value')}>
                {shecoData.recovery3}
                <span className={cx('unit')}>%</span>
              </div>
            </div>
            <div className={cx('item')}>
              <MdOutlineFlipCameraAndroid
                size={24}
                color="white"
                className={cx('rotate')}
                style={{ animationDuration: getAnimationDuration(shecoData.recovery4) }}
              />
              <div className={cx('value')}>
                {shecoData.recovery4}
                <span className={cx('unit')}>%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Monitor;
